/**
 * CR459: Hook for managing Merchant Sources linked to a specific adapter.
 * CR460-P: Null guard — safe when adapterKey is null (no-ops).
 * Provides:
 *   - assignedSources: sources currently mapped to this adapter key
 *   - eligibleSources: FLYER sources NOT yet mapped to this adapter (for picker)
 *   - bulkAssign / unassign mutations
 */
import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface AdapterSourceRow {
  merchant_source_id: string | null;
  merchant_id: string | null;
  merchant_name: string | null;
  source_url: string | null;
  status: string | null;
  requires_region: boolean | null;
  default_postal_code: string | null;
  flyer_image_capture_enabled: boolean | null;
  flyer_image_capture_adapter_key: string | null;
  flyer_image_capture_params: unknown | null;
}

const SOURCE_SELECT =
  "merchant_source_id, merchant_id, merchant_name, source_url, status, requires_region, default_postal_code, flyer_image_capture_enabled, flyer_image_capture_adapter_key, flyer_image_capture_params" as const;

export function useAdapterSources(adapterKey: string | null) {
  const { toast } = useToast();
  const [assignedSources, setAssignedSources] = useState<AdapterSourceRow[]>([]);
  const [eligibleSources, setEligibleSources] = useState<AdapterSourceRow[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    // Guard inside callback — safe for React rules of hooks
    if (!adapterKey) return;
    setLoading(true);

    const [assignedRes, eligibleRes] = await Promise.all([
      supabase
        .from("v_flyer_image_capture_sources")
        .select(SOURCE_SELECT)
        .eq("flyer_image_capture_adapter_key", adapterKey)
        .order("merchant_id"),
      supabase
        .from("v_flyer_image_capture_sources")
        .select(SOURCE_SELECT)
        .or(`flyer_image_capture_adapter_key.is.null,flyer_image_capture_adapter_key.neq.${adapterKey}`)
        .order("merchant_id"),
    ]);

    if (assignedRes.error) {
      toast({ title: "Error loading assigned sources", description: assignedRes.error.message, variant: "destructive" });
    } else {
      setAssignedSources((assignedRes.data ?? []) as unknown as AdapterSourceRow[]);
    }

    if (eligibleRes.error) {
      toast({ title: "Error loading eligible sources", description: eligibleRes.error.message, variant: "destructive" });
    } else {
      setEligibleSources((eligibleRes.data ?? []) as unknown as AdapterSourceRow[]);
    }

    setLoading(false);
  }, [adapterKey, toast]);

  /**
   * Bulk-assign source IDs to this adapter.
   * - Sets adapter_key + enabled
   * - Only prefills params if source params are null/empty AND adapter has default_params
   */
  const bulkAssign = useCallback(
    async (
      sourceIds: string[],
      defaultParams: unknown | null
    ): Promise<boolean> => {
      if (!adapterKey || sourceIds.length === 0) return false;

      const { data: currentRows, error: fetchErr } = await supabase
        .from("merchant_sources")
        .select("id, flyer_image_capture_params")
        .in("id", sourceIds);

      if (fetchErr) {
        toast({ title: "Error assigning sources", description: fetchErr.message, variant: "destructive" });
        return false;
      }

      const errors: string[] = [];
      await Promise.all(
        (currentRows ?? []).map(async (row) => {
          const existingParams = row.flyer_image_capture_params;
          const hasParams =
            existingParams != null &&
            (typeof existingParams !== "object" || Object.keys(existingParams as object).length > 0);

          const { error } = await supabase
            .from("merchant_sources")
            .update({
              flyer_image_capture_adapter_key: adapterKey,
              flyer_image_capture_enabled: true,
              flyer_image_capture_params: (hasParams ? existingParams : defaultParams) as any,
              updated_at: new Date().toISOString(),
            } as any)
            .eq("id", row.id);

          if (error) errors.push(error.message);
        })
      );

      if (errors.length > 0) {
        toast({ title: "Some assignments failed", description: errors.join("; "), variant: "destructive" });
        return false;
      }

      toast({ title: `Assigned ${sourceIds.length} source${sourceIds.length === 1 ? "" : "s"}` });
      await fetchAll();
      return true;
    },
    [adapterKey, fetchAll, toast]
  );

  /**
   * Unassign a single source from this adapter.
   * Clears adapter_key + disables capture. Does NOT clear params.
   */
  const unassign = useCallback(
    async (sourceId: string): Promise<boolean> => {
      if (!adapterKey) return false;

      const { error } = await supabase
        .from("merchant_sources")
        .update({
          flyer_image_capture_adapter_key: null,
          flyer_image_capture_enabled: false,
          updated_at: new Date().toISOString(),
        } as any)
        .eq("id", sourceId);

      if (error) {
        toast({ title: "Error unassigning source", description: error.message, variant: "destructive" });
        return false;
      }
      toast({ title: "Source unassigned" });
      await fetchAll();
      return true;
    },
    [adapterKey, fetchAll, toast]
  );

  return { assignedSources, eligibleSources, loading, fetchAll, bulkAssign, unassign };
}
